import { useApolloClient } from '@apollo/client';
import React from 'react';
import { TableSkeleton } from 'src/common';
import { Modifier } from 'src/components/Modifier';
import { convertToModifiers, convertToModifiersGql } from 'src/components/ProjectedRisk/util';
import { isQueryLoading } from 'src/graphql';
import { useControlModifierQuery } from 'src/graphql/_generated';
import { getPercentage } from 'src/utils/misc';
import useVersion from 'src/hooks/useVersion';

interface ControlLinkedScenarioExpandContentProps {
  controlId: string;
  scenarioId: string;
}

export const ControlLinkedScenarioExpandContent: React.FC<ControlLinkedScenarioExpandContentProps> = ({
  controlId,
  scenarioId,
}) => {
  const client = useApolloClient();
  const { version } = useVersion();

  const {
    data: controlModifierData,
    loading,
    networkStatus,
  } = useControlModifierQuery({
    variables: { id: controlId },
    fetchPolicy: 'no-cache',
  });

  const isLoading = isQueryLoading({ loading, data: controlModifierData, networkStatus });
  const controlData = controlModifierData?.control;

  if (isLoading || !controlData) return <TableSkeleton />;

  const rmp = controlData.riskManagementPlans.find((r) => r.scenario?.id === scenarioId);

  if (!rmp || !rmp.scenario) return null;

  const recommendedModifiers = rmp.recommendedModifiers ? convertToModifiers(rmp.recommendedModifiers) : null;

  const baseFrequencyTimes = getPercentage(
    rmp.scenario.baseRisk?.frequencyTimes,
    rmp.scenario.baseRisk?.frequencyYears,
  );

  // Note: Cumulative compounding is not available in this view because
  // we don't have access to other controls' modifiers for each scenario.
  // Both Modifier Impact and Cumulative Impact will show the effect on baseRisk.
  return (
    <Modifier
      startingValues={{
        frequency_times: baseFrequencyTimes,
        lower: rmp.scenario.baseRisk?.lower || 0,
        upper: rmp.scenario.baseRisk?.upper || 0,
      }}
      control={{
        id: controlData.id,
        strategy: controlData.strategy,
        status: controlData.status,
        modifiers: convertToModifiersGql(rmp.modifiers || []),
      }}
      recommendedModifiers={recommendedModifiers}
      projectedScenario={{
        id: rmp.scenario.id,
        upper: rmp.scenario.baseRisk?.upper || 0,
        lower: rmp.scenario.baseRisk?.lower || 0,
        frequency_years: rmp.scenario.baseRisk?.frequencyYears || 0,
        frequency_times: baseFrequencyTimes,
      }}
      disabled={!!version}
      onSave={async () => {
        await client.refetchQueries({ include: ['controlModifier'] });
      }}
      withCopy
    />
  );
};
